// Strict product search over the WHOLE catalogue.
// The store search only hands the theme a limited set of results, so matches could be missed.
// This script loads every published product once (/products.json, 250 per request, cached for the
// visit) and shows ALL products whose name has every searched word. Products that have the words
// together in order ("shoe rack") come first. A half-typed last word is completed to the most
// common matching name word ("sho" -> "shoe"). Plural "s" is ignored.
// Works for the search page and the header search dropdown: any [data-hbr-results] box renders
// the results and hides its Liquid fallback ([data-hbr-liquid]) once products are loaded.
const CACHE_KEY = 'hbr-search-products-v3';
// Words shoppers use for the same thing: "clothes hanging stand" also finds "Cloth Hanger Rack".
// The first word in each group is the shared key. Product tags are searched too, so a product
// tagged "clothes hanging stand" always shows for that search.
const SAME_WORDS = [
  ['cloth', 'clothe', 'clothing', 'garment', 'coat'],
  ['hang', 'hanging', 'hanger'],
  ['stand', 'rack', 'holder', 'organizer', 'oganizer', 'organiser'],
];
const SAME_KEY = new Map();
SAME_WORDS.forEach((group) => group.forEach((w) => SAME_KEY.set(w, group[0])));
const keyOf = (w) => SAME_KEY.get(w) || w;
let catalogPromise = null;

function normWords(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[-,()\/|&+.:;"'!?–—_*#\[\]]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w));
}

async function loadCatalog() {
  if (catalogPromise) return catalogPromise;
  catalogPromise = (async () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.time < 30 * 60 * 1000) return parsed.items;
      }
    } catch (e) { /* ignore */ }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const items = [];
    for (let page = 1; page <= 20; page++) {
      const res = await fetch(`${root}products.json?limit=250&page=${page}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('catalog');
      const data = await res.json();
      const list = data.products || [];
      for (const p of list) {
        const variants = p.variants || [];
        const prices = variants.map((v) => parseFloat(v.price)).filter((n) => !isNaN(n));
        const first = variants.find((v) => v.available) || variants[0] || {};
        items.push({
          title: p.title,
          tags: `${Array.isArray(p.tags) ? p.tags.join(' ') : String(p.tags || '')} ${p.product_type || ''}`,
          url: `${root}products/${p.handle}`,
          image: (p.images && p.images[0] && p.images[0].src) || '',
          price: prices.length ? Math.min(...prices) : parseFloat(first.price) || 0,
          compare: parseFloat(first.compare_at_price) || 0,
          available: variants.some((v) => v.available),
        });
      }
      if (list.length < 250) break;
    }
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), items })); } catch (e) { /* ignore */ }
    return items;
  })();
  catalogPromise.catch(() => { catalogPromise = null; });
  return catalogPromise;
}

function search(items, terms) {
  const vocabCount = new Map();
  const prepared = items.map((item) => {
    const words = normWords(item.title);
    const tagWords = normWords(item.tags);
    words.concat(tagWords).forEach((w) => vocabCount.set(w, (vocabCount.get(w) || 0) + 1));
    const keys = words.map(keyOf);
    return { item, keys, set: new Set(keys.concat(tagWords.map(keyOf))), joined: ` ${keys.join(' ')} ` };
  });
  const fixed = normWords(terms).map((w) => {
    if (vocabCount.has(w)) return w;
    let best = w;
    let bestCount = 0;
    vocabCount.forEach((count, word) => {
      if (word.length > w.length && word.startsWith(w) && count > bestCount) {
        best = word;
        bestCount = count;
      }
    });
    return best;
  }).map(keyOf);
  if (!fixed.length) return [];
  const phrase = ` ${fixed.join(' ')} `;
  const together = [];
  const others = [];
  for (const p of prepared) {
    if (!fixed.every((w) => p.set.has(w))) continue;
    // Rank: searched word early in the name first ("Clothes Hanging Stand" before "...Cleaning Cloth Holder").
    const at = p.keys.indexOf(fixed[0]);
    p.item._rank = at < 0 ? 99 : at;
    if (fixed.length > 1 && p.joined.includes(phrase)) together.push(p.item);
    else others.push(p.item);
  }
  const byStock = (a, b) => (a._rank - b._rank) || (Number(b.available) - Number(a.available));
  return together.sort(byStock).concat(others.sort(byStock));
}

const money = (n) => `Rs.${Math.round(n).toLocaleString('en-US')}`;
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const sized = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');

function card(item, index, mode) {
  const sale = item.compare > item.price;
  const off = sale ? Math.round(((item.compare - item.price) / item.compare) * 100) : 0;
  const img = item.image
    ? `<img src="${esc(sized(item.image, 500))}" alt="${esc(item.title)}" loading="${index < 4 ? 'eager' : 'lazy'}" class="size-full object-contain p-2">`
    : '';
  const optionAttrs = mode === 'predictive' ? ` id="ps-option-${index}" role="option" aria-selected="false" data-ps-url="${esc(item.url)}"` : '';
  return `<li${optionAttrs}>
    <a href="${esc(item.url)}" class="group block no-underline">
      <span class="relative block aspect-square overflow-hidden rounded-card bg-[#F4F6F9]">${img}
        ${sale ? `<span class="absolute start-2 top-2 rounded-full bg-[#FF6A00] px-2 py-0.5 text-[0.6875rem] font-bold text-white">-${off}%</span>` : ''}
        ${item.available ? '' : '<span class="absolute end-2 top-2 rounded-full bg-[#153250] px-2 py-0.5 text-[0.6875rem] font-bold text-white">Sold out</span>'}
      </span>
      <span class="mt-2 block text-sm font-medium leading-snug text-on-surface line-clamp-2">${esc(item.title)}</span>
      <span class="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-sm">
        <span class="font-semibold text-[#153250]">${money(item.price)}</span>
        ${sale ? `<s class="text-xs text-on-surface-secondary">${money(item.compare)}</s>` : ''}
      </span>
    </a>
  </li>`;
}

async function renderBox(box) {
  const terms = (box.dataset.terms || '').trim();
  if (!terms || box.dataset.renderedFor === terms) return;
  box.dataset.renderedFor = terms;
  const mode = box.dataset.mode || 'page';
  const fallback = box.parentElement && box.parentElement.querySelector('[data-hbr-liquid]');
  // Hide the limited store results straight away and show a loading line until every product is in.
  if (fallback) fallback.hidden = true;
  box.innerHTML = '<p class="py-10 text-center text-sm text-on-surface-secondary" aria-live="polite">Searching all products…</p>';
  let items;
  try {
    items = await loadCatalog();
  } catch (e) {
    box.innerHTML = '';
    if (fallback) fallback.hidden = false; // keep the store results if the catalogue can't load
    return;
  }
  if (box.dataset.terms.trim() !== terms) return;
  const results = search(items, terms);
  const safeTerms = esc(terms);
  if (!results.length) {
    box.innerHTML = `<p class="text-on-surface-secondary py-10 text-center text-sm">No products found for “${safeTerms}”.</p>`;
    return;
  }
  const count = `${results.length} product${results.length === 1 ? '' : 's'} found for “${safeTerms}”`;
  const grid = mode === 'predictive'
    ? 'grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-3'
    : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-6 md:gap-x-4 md:gap-y-10';
  box.innerHTML = `<p class="mb-4 text-sm text-on-surface-secondary">${count}</p>
    <ul role="list" class="${grid}">${results.map((item, i) => card(item, i, mode)).join('')}</ul>`;
}

function scan() {
  document.querySelectorAll('[data-hbr-results]').forEach(renderBox);
}

let scheduled = false;
const observer = new MutationObserver(() => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    scan();
  });
});

function init() {
  scan();
  observer.observe(document.body, { childList: true, subtree: true });
  // Warm the catalogue when the shopper opens or focuses search, so results appear instantly.
  document.addEventListener('focusin', (event) => {
    if (event.target && event.target.matches && event.target.matches('input[type="search"], input[name="q"]')) {
      loadCatalog().catch(() => {});
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
